import React, { useState, useRef, useEffect } from 'react';
import { UserCircle2, UserRound, Palette, PaintBucket, CheckCircle2 } from 'lucide-react';
import { CV_TEMPLATES, TEMPLATE_CATEGORIES } from '../lib/cvTemplates';
import { CVTemplate, TemplateColors } from '../types';
import { COLOR_PALETTE, generateTemplateColors } from '../lib/colorUtils';
import { Template1 } from './templates/Template1';
import { Template2 } from './templates/Template2';
import { Template3 } from './templates/Template3';
import { Template4 } from './templates/Template4';
import { Template5 } from './templates/Template5';
import { Template6 } from './templates/Template6';
import { Template7 } from './templates/Template7';
import { Template8 } from './templates/Template8';

interface TemplateSelectorProps {
  selectedTemplate: string;
  onTemplateSelect: (template: any, color?: string) => void;
  category: string;
  userPhoto?: string | null;
}

// Mapeo de componentes de template
const templateComponents = {
  template1: Template1,
  template2: Template2,
  template3: Template3,
  template4: Template4,
  template5: Template5,
  template6: Template6,
  template7: Template7,
  template8: Template8
};

export function TemplateSelector({ 
  selectedTemplate, 
  onTemplateSelect, 
  category,
  userPhoto 
}: TemplateSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [selectedColor, setSelectedColor] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Estados para el manejo de notificaciones
  const [showColorNotification, setShowColorNotification] = useState(false);
  
  // Buscar el template seleccionado
  const currentTemplate = CV_TEMPLATES.find(t => t.id === selectedTemplate) || null;
  
  // Establecer el color por defecto al seleccionar un template
  useEffect(() => {
    if (currentTemplate && !selectedColor) {
      setSelectedColor(currentTemplate.defaultColor);
    }
  }, [currentTemplate, selectedColor]);

  // Manejadores para drag scroll
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current!.offsetLeft);
    setScrollLeft(scrollRef.current!.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current!.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current!.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Manejador para seleccionar un template
  const handleTemplateSelect = (template: CVTemplate) => {
    setSelectedColor(template.defaultColor);
    onTemplateSelect(template, template.defaultColor);
    setShowPreview(true);
  };
  
  // Manejador para cambiar el color (preventivo, sin notificación)
  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    // No notificamos al usuario durante la selección para evitar ruido visual
    if (currentTemplate) {
      onTemplateSelect(currentTemplate, color);
    }
  };
  
  // Manejador para confirmar el color seleccionado
  const handleColorConfirm = () => {
    if (currentTemplate && selectedColor) {
      // Aplicamos el color y mostramos una única notificación
      onTemplateSelect(currentTemplate, selectedColor);
      
      // Mostramos la notificación y la ocultamos después de 2 segundos
      setShowColorNotification(true);
      setTimeout(() => {
        setShowColorNotification(false);
      }, 2000);
    }
  };
  
  // Datos de ejemplo para la vista previa - Incluir foto del usuario si está disponible
  const previewData = {
    name: "Nombre Ejemplo",
    title: "Cargo Profesional",
    photo: userPhoto, // Usar la foto pasada como prop
    contact: {
      email: "ejemplo@email.com",
      phone: "+34 123 456 789",
      location: "Madrid, España"
    },
    about: "Profesional con experiencia en desarrollo de software y gestión de proyectos tecnológicos.",
    experience: [
      {
        title: "Desarrollador Senior",
        company: "Empresa Ejemplo S.L.",
        period: "2020 - Presente",
        description: "Desarrollo de aplicaciones web con React y Node.js.",
        achievements: ["Implementación de sistema de autenticación", "Optimización de rendimiento"],
        technologies: ["React", "Node.js", "TypeScript"]
      }
    ],
    education: [
      {
        degree: "Ingeniería Informática",
        institution: "Universidad Ejemplo",
        period: "2015 - 2019"
      }
    ],
    languages: [
      { name: "Español", level: "Nativo" },
      { name: "Inglés", level: "Avanzado" }
    ],
    skills: ["React", "JavaScript", "TypeScript", "Node.js", "CSS", "HTML"],
    courses: [],
    template: selectedTemplate
  };

  return (
    <div className="relative">
      <div className="mb-4">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <span>Categorías</span>
          <span className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
        </button>
        
        {isDropdownOpen && (
          <div className="absolute mt-2 w-48 bg-white rounded-lg shadow-lg z-10 overflow-hidden">
            {TEMPLATE_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  onTemplateSelect({ id: cat.id });
                  setIsDropdownOpen(false);
                }}
                className={`w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors ${
                  category === cat.id ? 'bg-blue-100' : ''
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selector de templates */}
      <div className="relative group">
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ 
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {CV_TEMPLATES.map((template, index) => (
            <div
              key={template.id}
              className="min-w-[220px] snap-center"
            >
              <button
                onClick={() => handleTemplateSelect(template)}
                className={`relative group w-full aspect-[3/4] rounded-lg overflow-hidden transition-transform hover:scale-105 ${
                  selectedTemplate === template.id
                    ? 'ring-4 ring-blue-500'
                    : 'hover:ring-2 hover:ring-blue-400'
                }`}
              >
                <img
                  src={template.image}
                  alt={template.name}
                  className={`w-full h-full ${index === 0 ? 'object-fill' : 'object-contain'}`}
                  draggable="false"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                    <h3 className="text-white font-semibold text-lg mb-1">{template.name}</h3>
                    <p className="text-gray-200 text-sm">{template.description}</p>
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>

        <div className="h-0.5 bg-gray-700/20 mt-4">
          <div
            className="h-full bg-blue-500/50 transition-all duration-300"
            style={{
              width: scrollRef.current
                ? `${(scrollRef.current.scrollLeft / (scrollRef.current.scrollWidth - scrollRef.current.clientWidth)) * 100}%`
                : '0%'
            }}
          />
        </div>

        <button
          onClick={() => scrollRef.current?.scrollBy({ left: -220, behavior: 'smooth' })}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 p-2 rounded-full text-white transition-colors opacity-0 group-hover:opacity-100"
        >
          ◀
        </button>
        <button
          onClick={() => scrollRef.current?.scrollBy({ left: 220, behavior: 'smooth' })}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 p-2 rounded-full text-white transition-colors opacity-0 group-hover:opacity-100"
        >
          ▶
        </button>
      </div>
      
      {/* Vista previa y selección de color */}
      {selectedTemplate && (
        <div className="mt-6 animate-fadeIn">
          <div className="bg-blue-900/30 backdrop-blur-sm rounded-lg p-4 border border-blue-500/20 mb-4">
            <h3 className="text-lg font-medium text-blue-200 mb-2 flex items-center">
              <Palette className="w-5 h-5 mr-2" />
              Personaliza tu diseño
            </h3>
            <p className="text-purple-100/80 text-sm leading-relaxed mb-4">
              Selecciona un color que refleje tu personalidad profesional.
            </p>
            
            {/* Selector de colores */}
            <div className="flex flex-wrap gap-2 mb-4">
              {COLOR_PALETTE.map((color) => (
                <button
                  key={color}
                  onClick={() => {
                    // Cambiamos el color sin mostrar notificación
                    handleColorChange(color);
                  }}
                  className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                    selectedColor === color ? 'ring-2 ring-white scale-110' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  aria-label={`Color ${color}`}
                  title="Haz clic para aplicar este color"
                />
              ))}
            </div>
            
            {/* Selector de color personalizado mejorado */}
            <div className="flex flex-col gap-2 mt-3 bg-blue-800/30 p-3 rounded-lg border border-blue-400/20">
              <label htmlFor="custom-color" className="text-white text-sm flex items-center gap-2">
                <PaintBucket className="w-4 h-4 text-blue-300" />
                <span className="font-medium">Personalizar color</span>
              </label>
              <div className="flex items-center gap-3">
                <div className="relative group">
                  <input
                    id="custom-color"
                    type="color"
                    value={selectedColor}
                    onChange={(e) => {
                      // Aplicar el color automáticamente al cambiar
                      handleColorChange(e.target.value);
                    }}
                    className="h-10 w-20 cursor-pointer rounded-md border-2 border-white/30 transition-all group-hover:border-white/70"
                    title="Haz clic para abrir el selector de colores"
                  />
                  <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-all">
                    +
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="text-blue-200 text-xs mb-1">Código de color:</span>
                  <code className="bg-blue-950/50 text-xs px-2 py-1 rounded text-white font-mono">
                    {selectedColor.toUpperCase()}
                  </code>
                </div>
              </div>
              
              {/* Notificación única controlada */}
              {showColorNotification && (
                <div className="mt-2 bg-green-600/20 border border-green-500/30 text-green-400 px-3 py-2 rounded-md flex items-center text-sm">
                  <CheckCircle2 className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>¡Diseño actualizado correctamente!</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Vista previa grande */}
          <div className="bg-blue-900/30 backdrop-blur-sm rounded-lg p-4 border border-blue-500/20 mb-4 flex justify-center">
            {/* Contenedor exterior: Define el marco visible y recorta - Versión proporcionada por usuario */}
            <div className="rounded-lg overflow-hidden border border-gray-300 shadow-lg 
                          max-w-xs h-[500px]         /* Móvil: Ancho XS, Altura 500px */
                          md:max-w-none md:h-[450px] /* Web: Resetear ancho, Altura web */
                         "
            >
              {selectedTemplate && currentTemplate && (
                /* Contenedor interno: Tamaño completo, PERO escalado en móvil (más pequeño) */
                <div className="w-full h-[800px] bg-white
                              transform scale-[0.6] origin-top /* Móvil: Escalar más pequeño */
                              md:scale-100 /* Web: Resetear escala */
                             "
                >
                  {selectedTemplate === 'template1' && <Template1 data={previewData} customColor={selectedColor} />}
                  {selectedTemplate === 'template2' && <Template2 data={previewData} customColor={selectedColor} />}
                  {selectedTemplate === 'template3' && <Template3 data={previewData} customColor={selectedColor} />}
                  {selectedTemplate === 'template4' && <Template4 data={previewData} customColor={selectedColor} />}
                  {selectedTemplate === 'template5' && <Template5 data={previewData} customColor={selectedColor} />}
                  {selectedTemplate === 'template6' && <Template6 data={previewData} customColor={selectedColor} />}
                  {selectedTemplate === 'template7' && <Template7 data={previewData} customColor={selectedColor} />}
                  {selectedTemplate === 'template8' && <Template8 data={previewData} customColor={selectedColor} />}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}